import { Link, useParams } from "react-router-dom";
import {
    Box,
    Button,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    CircularProgress,
} from "@mui/material";
import { EditOutlined, CheckCircle, Cancel, PrintOutlined, ReplayOutlined, RemoveCircleOutline, ImageOutlined } from "@mui/icons-material";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import VehicleHiringEntryBasicDetail from "./VehicleHiringEntryComponents/VehicleHiringEntryBasicDetail";
import VehicleHiringEntryAttendanceCalendar from "./VehicleHiringEntryComponents/VehicleHiringEntryAttendanceCalendar";
import VehicleHiringEntryDetailTab from "./VehicleHiringEntryComponents/VehicleHiringEntryDetailTab";
import VehicleHiringEntryResubmitDialog from "./VehicleHiringEntryComponents/VehicleHiringEntryResubmitDialog";
import {
    useGetVehicleHiringEntryDetailQuery,
    useReviewVehicleHiringEntryMutation,
    useWithdrawVehicleHiringEntryMutation,
} from "store/services/vehicleHiringEntryApi";
import { useAddNoteSourceMutation } from "store/services/noteApi";
import { generateVehicleHiringPOR, generateVehicleHiringPORImage } from "./VehicleHiringEntryComponents/VehicleHiringEntryPORPrint";
import {
    VehicleHiringEntryDetailContext,
    useVehicleHiringEntryDetailContext,
} from "contexts/VehicleHiringEntryDetailContext";
import { useAuth } from "hooks/useAuth";
import { toast } from "react-toastify";
import { useState } from "react";

const VehicleHiringEntryDetailScreen = () => {
    const { id } = useParams();
    const breadcrumbs = [
        { title: "Vehicle Hiring Entry", path: "/app/crm/vehicle-hiring-entry/list" },
        { title: "Hiring Entry Detail" },
    ];

    const {
        data: vehicleHiringEntryDetail,
        isLoading: loadingVehicleHiringEntry,
        error,
    } = useGetVehicleHiringEntryDetailQuery(id);

    return (
        <VehicleHiringEntryDetailContext.Provider
            value={{ vehicleHiringEntryDetail, loadingVehicleHiringEntry }}
        >
            <BreadcrumbLayout
                breadcrumbs={breadcrumbs}
                isBusy={loadingVehicleHiringEntry}
                headerActions={<PageActions />}
            >
                <BreadcrumbLayout.Error error={error} />
                <BreadcrumbLayout.Paper>
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", md: "7fr 3fr" },
                            gap: 3,
                        }}
                    >
                        <Box sx={{ minWidth: 0 }}>
                            <VehicleHiringEntryBasicDetail />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                            <VehicleHiringEntryAttendanceCalendar entryDetail={vehicleHiringEntryDetail} />
                        </Box>
                    </Box>
                    <Divider />
                    <VehicleHiringEntryDetailTab />
                </BreadcrumbLayout.Paper>
            </BreadcrumbLayout>
        </VehicleHiringEntryDetailContext.Provider>
    );
};

export default VehicleHiringEntryDetailScreen;

const PageActions = () => {
    const { vehicleHiringEntryDetail } = useVehicleHiringEntryDetailContext();
    const { loggedInUser } = useAuth(); // <-- Get logged in user
    const [reviewEntry, { isLoading: isReviewing }] = useReviewVehicleHiringEntryMutation();
    const [withdrawEntry, { isLoading: isWithdrawing }] = useWithdrawVehicleHiringEntryMutation();
    const [addNoteSource] = useAddNoteSourceMutation();

    const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
    const [resubmitDialogOpen, setResubmitDialogOpen] = useState(false);
    const [noteText, setNoteText] = useState("");
    const [noteError, setNoteError] = useState("");
    const [isSaving, setIsSaving] = useState(false);

    const [withdrawDialogOpen, setWithdrawDialogOpen] = useState(false);
    const [withdrawNoteText, setWithdrawNoteText] = useState("");
    const [withdrawNoteError, setWithdrawNoteError] = useState("");
    const [isSavingWithdraw, setIsSavingWithdraw] = useState(false);

    const [isDownloadingImage, setIsDownloadingImage] = useState(false);

    const handleDownloadImage = async () => {
        setIsDownloadingImage(true);
        try {
            await generateVehicleHiringPORImage(vehicleHiringEntryDetail);
        } catch (err) {
            toast.error("Failed to generate image");
        } finally {
            setIsDownloadingImage(false);
        }
    };

    const isCreator = vehicleHiringEntryDetail?.createdBy?._id === loggedInUser?._id;
    const isResponsible = vehicleHiringEntryDetail?.responsibleUser?._id === loggedInUser?._id;
    const openRejectDialog = () => {
        setNoteText("");
        setNoteError("");
        setRejectDialogOpen(true);
    };

    const handleSubmitReject = async () => {
        if (noteText.trim().length < 3) {
            setNoteError("Note must be at least 3 characters.");
            return;
        }
        setIsSaving(true);
        try {
            await addNoteSource({
                content: `Rejection Reason: ${noteText.trim()}`,
                type: "Note",
                source: "VehicleHiringEntry",
                sourceRef: vehicleHiringEntryDetail._id,
            }).unwrap();
            await reviewEntry({
                id: vehicleHiringEntryDetail._id,
                approvalStatus: "Rejected",
            }).unwrap();
            toast.success("Entry Rejected successfully!");
            setRejectDialogOpen(false);
        } catch (err) {
            toast.error(err?.data?.message ?? "Failed to update status");
        } finally {
            setIsSaving(false);
        }
    };

    const handleApprove = async () => {
        try {
            await reviewEntry({
                id: vehicleHiringEntryDetail?._id,
                approvalStatus: "Approved",
            }).unwrap();
            toast.success("Entry Approved successfully!");
        } catch (err) {
            toast.error(err?.data?.message ?? "Failed to update status");
        }
    };

    const openWithdrawDialog = () => {
        setWithdrawNoteText("");
        setWithdrawNoteError("");
        setWithdrawDialogOpen(true);
    };

    const handleSubmitWithdraw = async () => {
        if (withdrawNoteText.trim().length < 3) {
            setWithdrawNoteError("Note must be at least 3 characters.");
            return;
        }
        setIsSavingWithdraw(true);
        try {
            await addNoteSource({
                content: `Withdraw Reason: ${withdrawNoteText.trim()}`,
                type: "Note",
                source: "VehicleHiringEntry",
                sourceRef: vehicleHiringEntryDetail._id,
            }).unwrap();
            await withdrawEntry(vehicleHiringEntryDetail._id).unwrap();
            toast.success("Entry Withdrawn successfully!");
            setWithdrawDialogOpen(false);
        } catch (err) {
            toast.error(err?.data?.message ?? "Failed to withdraw entry");
        } finally {
            setIsSavingWithdraw(false);
        }
    };

    return (
        <div className="flex items-center gap-4">
            {vehicleHiringEntryDetail?.approvalStatus === "Approved" && (
                <>
                    <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<PrintOutlined />}
                        onClick={() => generateVehicleHiringPOR(vehicleHiringEntryDetail)}
                        disabled={!vehicleHiringEntryDetail}
                    >
                        Print POR
                    </Button>
                    <Button
                        variant="outlined"
                        color="primary"
                        startIcon={isDownloadingImage ? <CircularProgress size={16} /> : <ImageOutlined />}
                        onClick={handleDownloadImage}
                        disabled={!vehicleHiringEntryDetail || isDownloadingImage}
                    >
                        {isDownloadingImage ? "Generating..." : "Download Image"}
                    </Button>
                </>
            )}

            {/* APPROVE / REJECT - Only visible to the Approve / Submitted To user */}
            {isResponsible && vehicleHiringEntryDetail?.approvalStatus === "Pending" && (
                <>
                    <Button
                        variant="outlined"
                        color="success"
                        startIcon={<CheckCircle />}
                        onClick={handleApprove}
                        disabled={isReviewing}
                    >
                        Approve
                    </Button>
                    <Button
                        variant="outlined"
                        color="error"
                        startIcon={<Cancel />}
                        onClick={openRejectDialog}
                        disabled={isReviewing}
                    >
                        Reject
                    </Button>
                </>
            )}

            {/* RESUBMIT (from Rejected) - Only visible to creator */}
            {isCreator && vehicleHiringEntryDetail?.approvalStatus === "Rejected" && (
                <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<ReplayOutlined />}
                    onClick={() => setResubmitDialogOpen(true)}
                >
                    Resubmit
                </Button>
            )}

            {/* WITHDRAW - Only visible to creator */}
            {isCreator && vehicleHiringEntryDetail?.isActive && vehicleHiringEntryDetail?.approvalStatus !== "Rejected" && (
                <Button
                    variant="outlined"
                    color="warning"
                    startIcon={<RemoveCircleOutline />}
                    onClick={openWithdrawDialog}
                    disabled={isWithdrawing}
                >
                    Withdraw
                </Button>
            )}

            {/* RESUBMIT (from Inactive/Withdrawn) - Only visible to creator */}
            {isCreator && !vehicleHiringEntryDetail?.isActive && (
                <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<ReplayOutlined />}
                    onClick={() => setResubmitDialogOpen(true)}
                >
                    Resubmit
                </Button>
            )}

            <Link to={`/app/crm/vehicle-hiring-entry/${vehicleHiringEntryDetail?._id}/edit`}>
                <Button startIcon={<EditOutlined />} variant="contained">
                    Edit
                </Button>
            </Link>

            <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Rejection Note</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        fullWidth
                        multiline
                        rows={3}
                        margin="dense"
                        label="Reason for Rejection"
                        value={noteText}
                        onChange={(e) => {
                            setNoteText(e.target.value);
                            if (noteError) setNoteError("");
                        }}
                        error={!!noteError}
                        helperText={noteError || "Minimum 3 characters."}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRejectDialogOpen(false)} disabled={isSaving}>
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={handleSubmitReject} disabled={isSaving}>
                        {isSaving ? <CircularProgress size={20} /> : "Submit"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={withdrawDialogOpen} onClose={() => setWithdrawDialogOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Withdraw Note</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        fullWidth
                        multiline
                        rows={3}
                        margin="dense"
                        label="Reason for Withdrawal"
                        value={withdrawNoteText}
                        onChange={(e) => {
                            setWithdrawNoteText(e.target.value);
                            if (withdrawNoteError) setWithdrawNoteError("");
                        }}
                        error={!!withdrawNoteError}
                        helperText={withdrawNoteError || "Minimum 3 characters."}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setWithdrawDialogOpen(false)} disabled={isSavingWithdraw}>
                        Cancel
                    </Button>
                    <Button variant="contained" color="warning" onClick={handleSubmitWithdraw} disabled={isSavingWithdraw}>
                        {isSavingWithdraw ? <CircularProgress size={20} /> : "Withdraw"}
                    </Button>
                </DialogActions>
            </Dialog>

            <VehicleHiringEntryResubmitDialog
                open={resubmitDialogOpen}
                entry={vehicleHiringEntryDetail}
                onClose={() => setResubmitDialogOpen(false)}
            />
        </div>
    );
};